import * as api from '../../api';
import { Tag } from '../../api';
import * as tagActions from './actions';
import { getSelectedIds, getTagsById } from './selectors';
import { getSelectedIds as getProjectIds } from '../project/selectors';
import { message } from '@myua/kit';
import { getSelectedId as getCategoryId } from '../category/selectors';
import { getSelectedIds as getExtensionIds } from '../extension/selectors';
import { getSearchApiParams } from '../search/selectors';
import { ALL_CATEGORIES_ID } from '../category/model';
import { ALL_EXTENSIONS_ID } from '../extension/model';
import * as lastStateActions from '../lastState/actions';
import { getViewMode } from '../viewMode/selectors';
import { ViewMode } from '../viewMode/model';
import { getCreative } from '../viewer/selectors';
import { takeLatest, takeEvery } from '../../listenerMiddleware';
import { getPermissions } from '../app/selectors';
import { Permission } from '../../../general/model';
import { loadDictionary } from '../filtersCache/loadDictionary';

export const tagListeners = () => {
  watchLoadTagsRequested();
  watchLastState();
  watchTagRequested();
};

function watchLoadTagsRequested() {
  takeLatest(tagActions.loadTagsRequested, async ({ payload }, { getState, dispatch, signal }) => {
    const permissions = getPermissions(getState());
    if (!permissions[Permission['fs.assetlab_access']]) {
      return;
    }

    const projectIds = getProjectIds(getState());
    const categoryId = getCategoryId(getState());
    const extensionIds = getExtensionIds(getState());
    const searchApiParams = getSearchApiParams(getState());
    const viewMode = getViewMode(getState());
    const creative = getCreative(getState());

    const requestParams = {
      projectIds,
      categoryId: categoryId === ALL_CATEGORIES_ID ? undefined : categoryId,
      extensionIds: extensionIds.includes(ALL_EXTENSIONS_ID) ? undefined : extensionIds,
      ...searchApiParams,
      showCreatives: viewMode === ViewMode.creatives ? true : undefined,
      creativeId: creative?.id,
    };

    await loadDictionary(
      {
        dict: 'tag',
        params: requestParams,
        setLoading: tagActions.setLoading,
        itemsChanged: (tags) => tagActions.tagsChanged({ tags }),
        request: () =>
          api.loadTags(requestParams).then((r) => (r.success ? r.data.response : undefined)),
        errorMessage: 'Tags have not been loaded',
      },
      { getState, dispatch, signal },
    );
    if (signal.aborted) {
      return;
    }

    // Проверки консистентности идут всегда, даже когда сеть была пропущена.
    const { saveLastState } = payload;
    // Если ранее были выбраны теги, которые теперь отсутствуют среди доступных тегов,
    // то надо обновить выбранные теги. Такое может случиться, если мы не меняли проект,
    // но меняли фильтры, и по какой-то причине с бэка пришел другой список тегов.
    // Другой случай - когда в lastState сохранен id, отсутствующий в списке тегов.
    const selectedIds = getSelectedIds(getState());
    const tagsById = getTagsById(getState());
    const newSelectedTags = selectedIds
      .map((id) => tagsById[id])
      .filter((tag): tag is Tag => Boolean(tag));
    const newSelectedIds = newSelectedTags.map(({ id }) => id);
    if (newSelectedIds.length !== selectedIds.length) {
      dispatch(
        tagActions.tagsSelected({
          ids: newSelectedIds,
          saveLastState,
          log: { tags: newSelectedTags },
        }),
      );
    }
  });
}

function watchLastState() {
  takeLatest(tagActions.tagsSelected, ({ payload }, { dispatch }) => {
    if (payload.saveLastState) {
      dispatch(lastStateActions.saveRequested());
    }
  });

  takeLatest(tagActions.expandedModeChanged, (action, { dispatch }) => {
    dispatch(lastStateActions.saveRequested());
  });
}

function watchTagRequested() {
  takeEvery(tagActions.tagRequested, async ({ payload }) => {
    const messageKey = `tag.append.${new Date().valueOf()}`;
    void message.loading({
      key: messageKey,
      content: `Submitting new tag: "${payload.name}" ...`,
      duration: 0,
    });
    const result = await api.appendTag(payload.name);
    if (result.success) {
      void message.success({
        key: messageKey,
        content: `Tag has been submitted: "${payload.name}"`,
      });
    } else {
      const content =
        (result.reason as any).response?.detail ?? `Tag has not been submitted: "${payload.name}"`;
      void message.error({ key: messageKey, content });
    }
  });
}
